import { useMemo } from "react";
import { API } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Copy, MessageCircle, Mail, ExternalLink, Zap, Tag, Building2, ShieldCheck,
  Activity, FileText, Lock, ListChecks, SlidersHorizontal, Users, Radio,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useClient } from "@/context/ClientContext";
import LeadSettings from "@/pages/LeadSettings";
import AutomationsPanel from "@/components/AutomationsPanel";
import LookupManager from "@/components/LookupManager";
import TaxonomyManager from "@/components/TaxonomyManager";
import PermissionMatrix from "@/components/PermissionMatrix";
import ActivityLog from "@/components/ActivityLog";
import EmailTemplateManager from "@/components/EmailTemplateManager";
import UsersTable from "@/components/settings/UsersTable";
import ChannelsTable from "@/components/settings/ChannelsTable";
import DepartmentsManager from "@/components/settings/DepartmentsManager";
import DynamicFieldsManager from "@/components/settings/DynamicFieldsManager";
import SlaPoliciesManager from "@/components/settings/SlaPoliciesManager";
import WaTemplatesManager from "@/components/settings/WaTemplatesManager";
import CountryCodesManager from "@/components/settings/CountryCodesManager";
import TenantMigrationsCard from "@/components/settings/TenantMigrationsCard";

// Section → canonical "view" permission(s) that make it visible.
// Each section is gated by a single canonical view key (added to the catalog
// where one did not previously exist). Activity is the one exception: it stays
// visible for ANY logs.* read permission, matching the broadened /audit-log guard.
const TAB_PERMS = {
  users: ["users.view"],
  permissions: ["users.manage_permissions"],
  activity: ["logs.view_login", "logs.view_error", "logs.view_change", "logs.view_email", "logs.export"],
  taxonomy: ["catalogue.view_categories"],
  lookups: ["catalogue.view_lookups"],
  dynamic_forms: ["dynamic_fields.view"],
  sla: ["sla.view"],
  channels: ["catalogue.view_channels"],
  webhooks: ["integrations.view_webhooks"],
  templates: ["email_templates.view"],
  wa_templates: ["wa_templates.view"],
  automations: ["automation.view"],
};

// Section display metadata (labels, icons, stable test-ids preserved from prior layout).
const SECTION_META = {
  users: { label: "Users & Roles", testid: "tab-users", icon: Users },
  permissions: { label: "Permissions", testid: "tab-permissions", icon: ShieldCheck },
  activity: { label: "Activity", testid: "tab-activity", icon: Activity },
  taxonomy: { label: "Categories & Issues", testid: "tab-taxonomy", icon: Tag },
  lookups: { label: "Lookups", testid: "tab-lookups", icon: Building2 },
  dynamic_forms: { label: "Dynamic Forms", testid: "tab-dynamic-forms", icon: ListChecks },
  sla: { label: "SLA Policies", testid: "tab-sla", icon: SlidersHorizontal },
  channels: { label: "Channels", testid: "tab-channels", icon: Radio },
  webhooks: { label: "Webhooks", testid: "tab-webhooks", icon: ExternalLink },
  templates: { label: "Email Templates", testid: "tab-templates", icon: FileText },
  wa_templates: { label: "WhatsApp Templates", testid: "tab-wa-templates", icon: MessageCircle },
  automations: { label: "Automations", testid: "tab-automations", icon: Zap },
};

// Top-level groups → ordered child sections.
const GROUPS = [
  { key: "access", label: "Access & Security", icon: ShieldCheck, sections: ["users", "permissions", "activity"] },
  { key: "config", label: "Ticket Configuration", icon: SlidersHorizontal, sections: ["taxonomy", "lookups", "dynamic_forms", "sla"] },
  { key: "messaging", label: "Channels & Messaging", icon: MessageCircle, sections: ["channels", "webhooks", "templates", "wa_templates"] },
  { key: "automation", label: "Automation", icon: Zap, sections: ["automations"] },
];

const GROUP_DESC = {
  access: "Manage who can access Kippu, what each role can do, and review the full audit trail.",
  config: "Configure the building blocks of every ticket — categories, lookups, custom fields and SLAs.",
  messaging: "Set up channels, inbound webhooks and the templates used for email & WhatsApp replies.",
  automation: "Automate repetitive work with rules that react to ticket events.",
};

export default function Settings() {
  const { active } = useClient();
  // Ooredoo / Al-Meera get a purpose-built Settings shell.
  if (active?.kind === "leads") return <LeadSettings />;
  return <TicketingSettings />;
}

function TicketingSettings() {
  const { hasAnyPerm } = useAuth();

  // Visible sections (existing per-permission RBAC, unchanged).
  const visibleSections = useMemo(
    () => new Set(
      Object.entries(TAB_PERMS)
        .filter(([, perms]) => hasAnyPerm(perms))
        .map(([key]) => key)
    ),
    [hasAnyPerm]
  );

  // Visible groups + their visible sections, preserving declared order.
  const visibleGroups = useMemo(
    () => GROUPS
      .map((g) => ({ ...g, sections: g.sections.filter((s) => visibleSections.has(s)) }))
      .filter((g) => g.sections.length > 0),
    [visibleSections]
  );

  const whatsappWebhookUrl = `${API}/webhooks/whatsapp`;
  const emailWebhookUrl = `${API}/webhooks/email/inbound`;
  const copyLink = (url) => {
    navigator.clipboard.writeText(url).then(() => toast.success("Copied to clipboard"));
  };

  const renderSection = (key) => {
    switch (key) {
      case "users":
        return <UsersTable />;
      case "permissions":
        return (
          <div className="space-y-4">
            <Card className="border-slate-200 bg-indigo-50/40 border-indigo-100">
              <CardContent className="p-4 text-xs text-indigo-900">
                <strong>Role × Action matrix.</strong> Toggle a checkbox to grant or revoke a capability for that role. Super Admin always has every permission and cannot be changed. Changes apply immediately to all logged-in users on their next API call.
              </CardContent>
            </Card>
            <PermissionMatrix />
          </div>
        );
      case "activity":
        return (
          <div className="space-y-4">
            <TenantMigrationsCard />
            <Card className="border-slate-200 bg-indigo-50/40 border-indigo-100">
              <CardContent className="p-4 text-xs text-indigo-900">
                <strong>Activity timeline.</strong> Every ticket update, permission change, and configuration mutation is logged with the actor, timestamp, and a before / after diff. Use the filters and "Load more" to browse the full history.
              </CardContent>
            </Card>
            <ActivityLog />
          </div>
        );
      case "taxonomy":
        return <TaxonomyManager />;
      case "lookups":
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-slate-200"><CardContent className="p-5"><LookupManager title="Contact Modes" endpoint="/contact-modes" testId="contact-modes" perm="catalogue.manage_contact_modes" /></CardContent></Card>
            <Card className="border-slate-200"><CardContent className="p-5"><LookupManager title="Languages" endpoint="/languages" testId="languages" perm="catalogue.manage_languages" /></CardContent></Card>
            <Card className="border-slate-200 lg:col-span-2"><CardContent className="p-5"><DepartmentsManager /></CardContent></Card>
            <Card className="border-slate-200 lg:col-span-2"><CardContent className="p-5"><CountryCodesManager /></CardContent></Card>
            <Card className="border-slate-200"><CardContent className="p-5"><LookupManager title="Case Types" endpoint="/case-types" testId="case-types" perm="catalogue.manage_issue_types" /></CardContent></Card>
            <Card className="border-slate-200"><CardContent className="p-5"><LookupManager title="Branches" endpoint="/branches" testId="branches" perm="catalogue.manage_stores" /></CardContent></Card>
          </div>
        );
      case "dynamic_forms":
        return <DynamicFieldsManager />;
      case "sla":
        return <SlaPoliciesManager />;
      case "channels":
        return <ChannelsTable />;
      case "automations":
        return <AutomationsPanel />;
      case "templates":
        return (
          <div className="space-y-4">
            <Card className="border-slate-200 bg-indigo-50/40 border-indigo-100">
              <CardContent className="p-4 text-xs text-indigo-900">
                <strong>Email templates.</strong> Each template is fired by the system at a specific moment (login OTP, ticket created, status change, reminder, SLA breach, dept magic-link, cancellation). Use the variable chips to insert dynamic data; the preview renders against a real recent ticket.
              </CardContent>
            </Card>
            <EmailTemplateManager />
          </div>
        );
      case "wa_templates":
        return <WaTemplatesManager />;
      case "webhooks":
        return (
          <div className="space-y-4">
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-base flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-emerald-600" /> WhatsApp Cloud API
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Callback URL</Label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Input readOnly value={whatsappWebhookUrl} className="font-mono text-xs" data-testid="whatsapp-webhook-url" />
                    <Button size="sm" variant="outline" onClick={() => copyLink(whatsappWebhookUrl)} data-testid="whatsapp-webhook-copy">
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Verify token</Label>
                  <p className="text-xs text-slate-500 mt-1">
                    Paste this value in Meta Developers → WhatsApp → Configuration → Webhook → Verify Token. Configured via <code className="font-mono bg-slate-100 px-1 py-0.5 rounded">WHATSAPP_VERIFY_TOKEN</code> on the server.
                  </p>
                </div>
                <ol className="text-xs text-slate-600 list-decimal pl-5 space-y-1.5">
                  <li>Create a Meta App with WhatsApp Business product.</li>
                  <li>Set <code className="font-mono bg-slate-100 px-1 rounded">WHATSAPP_APP_SECRET</code> &amp; <code className="font-mono bg-slate-100 px-1 rounded">WHATSAPP_VERIFY_TOKEN</code> in backend env.</li>
                  <li>Configure the callback URL above and click <em>Verify &amp; Save</em>.</li>
                  <li>Subscribe to the <code className="font-mono bg-slate-100 px-1 rounded">messages</code> field.</li>
                  <li>Inbound WhatsApp messages will auto-create tickets on the WhatsApp channel.</li>
                </ol>
                <a href="https://developers.facebook.com/docs/whatsapp/cloud-api/webhooks" target="_blank" rel="noreferrer" className="inline-flex items-center text-xs text-indigo-600 hover:text-indigo-700">
                  Meta WhatsApp webhook docs <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-base flex items-center gap-2">
                  <Mail className="w-4 h-4 text-indigo-600" /> Email-to-Ticket (Resend Inbound)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Inbound Webhook URL</Label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Input readOnly value={emailWebhookUrl} className="font-mono text-xs" data-testid="email-webhook-url" />
                    <Button size="sm" variant="outline" onClick={() => copyLink(emailWebhookUrl)} data-testid="email-webhook-copy">
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                <ol className="text-xs text-slate-600 list-decimal pl-5 space-y-1.5">
                  <li>In Resend dashboard → Inbound Emails, add a route for <code className="font-mono bg-slate-100 px-1 rounded">support@kippu.zefficacy.com</code>.</li>
                  <li>Set destination to: <code className="font-mono bg-slate-100 px-1 rounded">{emailWebhookUrl}</code></li>
                  <li>(Optional) set <code className="font-mono bg-slate-100 px-1 rounded">RESEND_INBOUND_SECRET</code> in backend env and add the same header value in Resend.</li>
                  <li>Customer emails will land as Email-channel tickets automatically.</li>
                </ol>
              </CardContent>
            </Card>
          </div>
        );
      default:
        return null;
    }
  };

  if (visibleGroups.length === 0) {
    return (
      <div className="max-w-md mx-auto mt-24 text-center" data-testid="settings-empty-state">
        <Lock className="w-10 h-10 text-slate-300 mx-auto mb-4" />
        <h2 className="font-heading text-xl font-semibold text-slate-700">No settings available</h2>
        <p className="text-sm text-slate-500 mt-2">
          Your current role doesn't have access to any Settings section. Ask a super admin to grant you the relevant permissions.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="settings-page">
      <Tabs defaultValue={visibleGroups[0].key}>
        <TabsList className="bg-white border border-slate-200 flex-wrap h-auto p-1.5 gap-1" data-testid="settings-group-tabs">
          {visibleGroups.map((g) => {
            const Icon = g.icon;
            return (
              <TabsTrigger
                key={g.key}
                value={g.key}
                data-testid={`settings-group-${g.key}`}
                className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white px-4 py-2 text-sm font-medium"
              >
                <Icon className="w-4 h-4 mr-2" />{g.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {visibleGroups.map((g) => (
          <TabsContent key={g.key} value={g.key} className="mt-5">
            <p className="text-sm text-slate-500 mb-4 max-w-2xl" data-testid={`settings-group-desc-${g.key}`}>
              {GROUP_DESC[g.key]}
            </p>
            <Tabs defaultValue={g.sections[0]}>
              <TabsList className="bg-slate-100/80 border border-slate-200 flex-wrap h-auto" data-testid={`settings-subtabs-${g.key}`}>
                {g.sections.map((s) => {
                  const meta = SECTION_META[s];
                  const Icon = meta.icon;
                  return (
                    <TabsTrigger key={s} value={s} data-testid={meta.testid}>
                      {Icon && <Icon className="w-3.5 h-3.5 mr-1.5" />}{meta.label}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
              {g.sections.map((s) => (
                <TabsContent key={s} value={s} className="mt-5">
                  {renderSection(s)}
                </TabsContent>
              ))}
            </Tabs>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
